import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader } from '../../../foundation/altzero_base/base/components/ui/card';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import { Textarea } from '../../../foundation/altzero_base/base/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../foundation/altzero_base/base/components/ui/select';
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Trash2,
  Star,
  Zap,
  DollarSign,
  Leaf,
  Building
} from 'lucide-react';
import {
  Offer,
  MultiYearRates,
  EnvironmentalCharges,
  NonEnergyCharges,
  RateType,
  MultiYearRateData,
  RateTypeData,
  CONTRACT_LENGTHS,
  ContractLength,
  createEmptyMultiYearRates,
  createEmptyEnvironmentalCharges,
  createEmptyEnvironmentalChargeYearData,
  createEmptyNonEnergyCharges
} from '../types/offerTypes';


import DualRateTable from './DualRateTable';
import { useDocumentForm } from '../contexts/DocumentFormContext';
import { getActiveRetailers, Retailer } from '../services/retailerService';
import { NON_ENERGY_KEY_MAP, normalizeEnvironmentalCharges } from '../lib/normalizeCharges';
import { resolveEnvDisplayKey } from '../lib/envSchemeRegistry';
import { usePowerEnvDefaults } from '../hooks/usePowerEnvDefaults';

interface OfferManagementCardProps {
  offer: Offer;
  index: number;
  isExpanded: boolean;
  onUpdate: (offer: Offer) => void;
  onRemove: (offerId: string) => void;
  onCopy: (offerId: string) => void;
  onToggleExpansion: (offerId: string) => void;
}

const OfferManagementCard: React.FC<OfferManagementCardProps> = ({
  offer,
  index,
  isExpanded,
  onUpdate,
  onRemove,
  onCopy,
  onToggleExpansion,
}) => {
  // Get context for immediate persistence
  const { updateOfferRateData, offers } = useDocumentForm();
  const powerEnvDefaults = usePowerEnvDefaults();
  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [loadingRetailers, setLoadingRetailers] = useState(true);
  const [formData, setFormData] = useState({
    retailerId: offer.retailerId || '',
    retailerName: offer.retailerName || '',
    offerType: offer.offerType || 'electricity',
    termMonths: offer.termMonths || 48, // Use offer's term or default to 4 years
    greenPercent: offer.greenPercent || 0,
    recommendedMonths: offer.recommendedMonths || [],
  });


  // Fetch active retailers from database
  useEffect(() => {
    const fetchRetailers = async () => {
      setLoadingRetailers(true);
      const result = await getActiveRetailers();
      if (result.success && result.data) {
        setRetailers(result.data);
      }
      setLoadingRetailers(false);
    };
    fetchRetailers();
  }, []);

  // Sync form data when offer changes
  useEffect(() => {
    setFormData({
      retailerId: offer.retailerId || '',
      retailerName: offer.retailerName || '',
      offerType: offer.offerType || 'electricity',
      termMonths: offer.termMonths || 48,
      greenPercent: offer.greenPercent || 0,
      recommendedMonths: offer.recommendedMonths || [],
    });

    // Initialize multiYearRateData from existing offer data
    if (offer.offerLines && offer.offerLines.length > 0) {
      const newRateData = {
        smooth: {
          peakRates: createEmptyMultiYearRates(),
          offpeakRates: createEmptyMultiYearRates(),
          shoulderRates: createEmptyMultiYearRates(),
          environmentalCharges: createEmptyEnvironmentalCharges(),
          nonEnergyCharges: createEmptyNonEnergyCharges()
        },
        stepped: {
          peakRates: createEmptyMultiYearRates(),
          offpeakRates: createEmptyMultiYearRates(),
          shoulderRates: createEmptyMultiYearRates(),
          environmentalCharges: createEmptyEnvironmentalCharges(),
          nonEnergyCharges: createEmptyNonEnergyCharges()
        }
      };

      // Populate from offerLines
      offer.offerLines.forEach(line => {
        const yearMatch = line.notes?.match(/Year (\d+)/);
        const year = yearMatch ? `year_${yearMatch[1]}` : 'year_1';

        if (line.component === 'peak_rate') {
          newRateData.smooth.peakRates[year as keyof MultiYearRates] = line.rate;
        } else if (line.component === 'offpeak_rate') {
          newRateData.smooth.offpeakRates[year as keyof MultiYearRates] = line.rate;
        } else if (line.component === 'shoulder_rate') {
          newRateData.smooth.shoulderRates[year as keyof MultiYearRates] = line.rate;
        }
      });

      // Populate from feesJson — registry-driven, so any scheme (LREC/SREC/VEEC plus
      // ESC/PRC/REPS/EEIS/... or an unrecognized custom key) routes to its correct bucket
      // instead of being matched by loose substring checks.
      if (offer.feesJson?.environmentalCharges) {
        Object.entries(offer.feesJson.environmentalCharges).forEach(([chargeType, rate]) => {
          const targetKey = resolveEnvDisplayKey(chargeType) as keyof EnvironmentalCharges;

          if (!newRateData.smooth.environmentalCharges[targetKey]) {
            newRateData.smooth.environmentalCharges[targetKey] = {};
          }
          if (!newRateData.stepped.environmentalCharges[targetKey]) {
            newRateData.stepped.environmentalCharges[targetKey] = {};
          }
          const chargeData = newRateData.smooth.environmentalCharges[targetKey]!;
          const steppedChargeData = newRateData.stepped.environmentalCharges[targetKey]!;
          (['year_1', 'year_2', 'year_3', 'year_4', 'year_5', 'year_6'] as const).forEach(year => {
            if (!chargeData[year]) chargeData[year] = createEmptyEnvironmentalChargeYearData();
            chargeData[year]!.ratePerKwh = rate as number;
            if (!steppedChargeData[year]) steppedChargeData[year] = createEmptyEnvironmentalChargeYearData();
            steppedChargeData[year]!.ratePerKwh = rate as number;
          });
        });
      }

      // Populate non-energy charges from feesJson
      if (offer.feesJson?.nonEnergyCharges) {
        Object.entries(offer.feesJson.nonEnergyCharges).forEach(([key, value]) => {
          // Map known keys or add as custom
          // createDefaultOffer uses "meterAgreement", "supplementaryMetering" (camelCase)
          // NonEnergyCharges uses "Meter Agreement", "Supplementary Metering" (Title Case with spaces)

          let targetKey = key;
          if (key === 'meterAgreement') targetKey = 'Meter Agreement';
          else if (key === 'supplementaryMetering') targetKey = 'Supplementary Metering';

          newRateData.smooth.nonEnergyCharges[targetKey] = value as number;
          newRateData.stepped.nonEnergyCharges[targetKey] = value as number;
        });
      }

      // Initialize rate data in context if not present
      if (!offer.multiYearRateData) {
        updateOfferRateData(offer.id, newRateData);
      }
    }
  }, [offer.id, offer.retailerId, offer.retailerName, offer.offerType, offer.greenPercent, offer.termMonths, offer.offerLines, offer.feesJson]);

  // Sync retailerId when retailers are loaded and offer has a retailerId
  useEffect(() => {
    if (!loadingRetailers && offer.retailerId) {
      // Always sync formData with offer.retailerId when retailers finish loading
      const retailer = retailers.find(r => r.id === offer.retailerId);
      setFormData(prev => {
        // Always update retailerId to match offer, and update name if retailer found
        const newRetailerName = retailer?.name || offer.retailerName || prev.retailerName;
        if (prev.retailerId !== offer.retailerId || prev.retailerName !== newRetailerName) {
          console.log('🔄 OfferManagementCard: Syncing retailer data', {
            offerRetailerId: offer.retailerId,
            offerRetailerName: offer.retailerName,
            foundRetailer: retailer?.name,
            newRetailerName,
            retailersCount: retailers.length
          });
          return {
            ...prev,
            retailerId: offer.retailerId,
            retailerName: newRetailerName,
          };
        }
        return prev;
      });
    } else if (!loadingRetailers && !offer.retailerId && formData.retailerId) {
      // Clear retailerId if offer doesn't have one
      setFormData(prev => ({
        ...prev,
        retailerId: '',
        retailerName: '',
      }));
    }
  }, [loadingRetailers, retailers, offer.retailerId, offer.retailerName]);

  // Separate effect to sync recommendedMonths only when offer ID changes (initial load)
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      recommendedMonths: offer.recommendedMonths || []
    }));
  }, [offer.id]); // Only when offer ID changes (new offer loaded)

  // Calculate contract years — prefer availableTerms/contract_terms over legacy termMonths
  const contractYears = useMemo(() => {
    if (offer.availableTerms && offer.availableTerms.length > 0) {
      return Math.max(1, Math.ceil(Math.max(...offer.availableTerms) / 12));
    }
    if (offer.extn?.contract_terms) {
      const keys = Object.keys(offer.extn.contract_terms);
      const maxMonths = Math.max(...keys.map(k => parseInt(k.split('_')[0], 10)).filter(n => !isNaN(n)));
      if (maxMonths > 0) return Math.max(1, Math.ceil(maxMonths / 12));
    }
    return Math.max(1, Math.ceil((formData.termMonths || 48) / 12));
  }, [offer.availableTerms, offer.extn?.contract_terms, formData.termMonths]);

  // Ensure multiYearRateData has complete structure
  // Use context offers as source of truth — the prop may be stale if OffersStep uses localOffers
  const liveOffer = offers.find(o => o.id === offer.id) || offer;
  const multiYearRateData = useMemo(() => {
    const defaults = {
      smooth: {
        peakRates: createEmptyMultiYearRates(),
        offpeakRates: createEmptyMultiYearRates(),
        shoulderRates: createEmptyMultiYearRates(),
        environmentalCharges: createEmptyEnvironmentalCharges(),
        nonEnergyCharges: createEmptyNonEnergyCharges()
      },
      stepped: {
        peakRates: createEmptyMultiYearRates(),
        offpeakRates: createEmptyMultiYearRates(),
        shoulderRates: createEmptyMultiYearRates(),
        environmentalCharges: createEmptyEnvironmentalCharges(),
        nonEnergyCharges: createEmptyNonEnergyCharges()
      }
    };

    if (!liveOffer.multiYearRateData) return defaults;

    // Helper to merge rates
    const mergeRates = (def: any, src: any) => ({ ...def, ...(src || {}) });

    // Helper to merge env charges - use normalizeEnvironmentalCharges for proper DB-to-UI transformation
    const mergeEnvCharges = (def: any, src: any) => {
      if (!src) return def;

      // Normalize the source data from DB format (lrec/sres/veec with snake_case fields)
      // to UI format (LREC/SREC/VEEC/ESS/REES with camelCase fields).
      // No `defaults` passed here deliberately — this offer's rates were already resolved once,
      // server-side, by offerAutoPopulateService (real parsed rate, formula-derived, or explicitly
      // left blank by the user in review). Passing defaults here would re-run the same derivation
      // formula on every render and silently overwrite a deliberate "leave this blank" decision
      // with a freshly re-computed number, undoing the user's review edit the moment this card
      // re-renders (e.g. right after Save & Approve, when the offer flows into this next step).
      const normalized = normalizeEnvironmentalCharges(src);
      if (!normalized) return def;

      // Merge normalized data with defaults — include ALL schemes (ESC, PRC, REPS, etc.)
      const result: Record<string, any> = { ...def };
      Object.keys(normalized).forEach(key => {
        result[key] = { ...(def[key] || {}), ...((normalized as any)[key] || {}) };
      });
      return result;
    };

    // Helper to merge non-energy charges with snake_case → Title Case normalization
    const mergeNonEnergyCharges = (def: Record<string, number | undefined>, src: Record<string, number | undefined> | undefined) => {
      const result = { ...def };
      if (src) {
        Object.entries(src).forEach(([key, value]) => {
          const normalizedKey = NON_ENERGY_KEY_MAP[key.toLowerCase()] || key;
          // Prefer non-zero values
          if (result[normalizedKey] === undefined || result[normalizedKey] === 0) {
            result[normalizedKey] = value;
          }
        });
      }
      return result;
    };

    // Environmental charges are shared between smooth and stepped — always derive from smooth source
    // so existing offers with empty stepped env charges still display correctly.
    const sharedEnvCharges = mergeEnvCharges(
      defaults.smooth.environmentalCharges,
      liveOffer.multiYearRateData.smooth?.environmentalCharges || liveOffer.multiYearRateData.stepped?.environmentalCharges
    );
    const sharedNonEnergyCharges = mergeNonEnergyCharges(
      defaults.smooth.nonEnergyCharges,
      liveOffer.multiYearRateData.smooth?.nonEnergyCharges || liveOffer.multiYearRateData.stepped?.nonEnergyCharges
    );

    return {
      smooth: {
        peakRates: mergeRates(defaults.smooth.peakRates, liveOffer.multiYearRateData.smooth?.peakRates),
        offpeakRates: mergeRates(defaults.smooth.offpeakRates, liveOffer.multiYearRateData.smooth?.offpeakRates),
        shoulderRates: mergeRates(defaults.smooth.shoulderRates, liveOffer.multiYearRateData.smooth?.shoulderRates),
        environmentalCharges: sharedEnvCharges,
        nonEnergyCharges: sharedNonEnergyCharges
      },
      stepped: {
        peakRates: mergeRates(defaults.stepped.peakRates, liveOffer.multiYearRateData.stepped?.peakRates),
        offpeakRates: mergeRates(defaults.stepped.offpeakRates, liveOffer.multiYearRateData.stepped?.offpeakRates),
        shoulderRates: mergeRates(defaults.stepped.shoulderRates, liveOffer.multiYearRateData.stepped?.shoulderRates),
        environmentalCharges: sharedEnvCharges,
        nonEnergyCharges: sharedNonEnergyCharges
      }
    };
  }, [liveOffer.multiYearRateData, powerEnvDefaults]);

  // No longer need rateType state since we show both simultaneously

  // Get current retailer info from database retailers
  const currentRetailer = retailers.find(r => r.id === formData.retailerId);

  // Create combined retailers list that includes current retailer if not in active list
  const displayRetailers = useMemo(() => {
    const activeRetailers = [...retailers];
    // If current retailer is not in active list, add it as a temporary entry
    if (formData.retailerId && formData.retailerName && !activeRetailers.find(r => r.id === formData.retailerId)) {
      activeRetailers.push({
        id: formData.retailerId,
        name: formData.retailerName,
        org_id: '',
        contact_email: null,
        logo: offer.retailerLogo || null,
        active: false,
        extn: {},
        created_at: new Date().toISOString(),
      });
    }
    return activeRetailers;
  }, [retailers, formData.retailerId, formData.retailerName, offer.retailerLogo]);

  // Simplified: Just use the existing offer structure for now
  // The main issue is recommendedMonths not persisting, not the detailed rates

  const updateFormData = (field: string, value: any) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    // Update the offer immediately - focus on the core fields first
    const updatedOffer: Offer = {
      ...offer,
      retailerId: newFormData.retailerId,
      retailerName: newFormData.retailerName,
      offerType: newFormData.offerType as 'electricity' | 'gas',
      termMonths: newFormData.termMonths,
      greenPercent: newFormData.greenPercent,
      recommendedMonths: newFormData.recommendedMonths,
      updatedAt: new Date(),
    };

    console.log("🔄 OfferManagementCard: updateFormData called", {
      field,
      value,
      newRecommendedMonths: newFormData.recommendedMonths,
      updatedOffer: { id: updatedOffer.id, retailerName: updatedOffer.retailerName, recommendedMonths: updatedOffer.recommendedMonths }
    });

    onUpdate(updatedOffer);
  };





  // Handle rate changes - supports both smooth and stepped
  const handleRateChange = (rateTypeKey: 'peak' | 'offpeak' | 'shoulder', year: string, value: number, isSmooth: boolean) => {
    const targetRateType = isSmooth ? 'smooth' : 'stepped';
    const newData = {
      ...multiYearRateData,
      [targetRateType]: {
        ...multiYearRateData[targetRateType],
        [`${rateTypeKey}Rates`]: {
          ...multiYearRateData[targetRateType][`${rateTypeKey}Rates` as keyof RateTypeData],
          [year]: value
        }
      }
    };

    console.log("🔄 OfferManagementCard: handleRateChange", {
      offerId: offer.id,
      rateTypeKey,
      year,
      value,
      targetRateType
    });

    // Immediately update context with persistence
    updateOfferRateData(offer.id, newData);
  };

  // Handle environmental charges change - environmental charges are shared across smooth and stepped
  const handleEnvironmentalChargeChange = (chargeType: string, year: string, field: 'pricePerCertificate' | 'powerPercentage' | 'ratePerKwh', value: number, _isSmooth: boolean) => {
    const updatedEnvCharges = (prev: EnvironmentalCharges) => ({
      ...prev,
      [chargeType]: {
        ...prev[chargeType],
        [year]: {
          ...prev[chargeType]?.[year],
          [field]: value
        }
      }
    });
    const newData = {
      ...multiYearRateData,
      smooth: {
        ...multiYearRateData.smooth,
        environmentalCharges: updatedEnvCharges(multiYearRateData.smooth.environmentalCharges)
      },
      stepped: {
        ...multiYearRateData.stepped,
        environmentalCharges: updatedEnvCharges(multiYearRateData.stepped.environmentalCharges)
      }
    };

    // Immediately update context with persistence
    updateOfferRateData(offer.id, newData);
  };

  const handleNonEnergyChargeChange = (chargeName: string, value: number, isSmooth: boolean) => {
    const targetRateType = isSmooth ? 'smooth' : 'stepped';
    const currentCharges = { ...multiYearRateData[targetRateType].nonEnergyCharges };

    // Remove any snake_case variant that maps to the same normalized key
    // This prevents ghost keys from accumulating in state
    Object.keys(currentCharges).forEach(existingKey => {
      if (existingKey !== chargeName &&
          NON_ENERGY_KEY_MAP[existingKey.toLowerCase()] === chargeName) {
        delete currentCharges[existingKey];
      }
    });

    currentCharges[chargeName] = value;

    const newData = {
      ...multiYearRateData,
      [targetRateType]: {
        ...multiYearRateData[targetRateType],
        nonEnergyCharges: currentCharges
      }
    };
    updateOfferRateData(offer.id, newData);
  };

  const handleAddCustomCharge = (chargeName: string, isSmooth: boolean) => {
    const targetRateType = isSmooth ? 'smooth' : 'stepped';
    const newData = {
      ...multiYearRateData,
      [targetRateType]: {
        ...multiYearRateData[targetRateType],
        nonEnergyCharges: {
          ...multiYearRateData[targetRateType].nonEnergyCharges,
          [chargeName]: 0
        }
      }
    };
    updateOfferRateData(offer.id, newData);
  };

  const handleRemoveCustomCharge = (chargeName: string, isSmooth: boolean) => {
    const targetRateType = isSmooth ? 'smooth' : 'stepped';
    const newNonEnergyCharges = { ...multiYearRateData[targetRateType].nonEnergyCharges };
    delete newNonEnergyCharges[chargeName];

    const newData = {
      ...multiYearRateData,
      [targetRateType]: {
        ...multiYearRateData[targetRateType],
        nonEnergyCharges: newNonEnergyCharges
      }
    };
    updateOfferRateData(offer.id, newData);
  };

  // Handle per-contract-term rate changes (updates extn.contract_terms)
  const handleContractTermRateChange = (termKey: string, yearKey: string, rateType: 'peak' | 'offpeak' | 'shoulder', value: number) => {
    const newExtn = {
      ...(offer.extn || {}),
      contract_terms: {
        ...(offer.extn?.contract_terms || {}),
        [termKey]: {
          ...(offer.extn?.contract_terms?.[termKey] || {}),
          [yearKey]: {
            ...(offer.extn?.contract_terms?.[termKey]?.[yearKey] || {}),
            [rateType]: value,
          },
        },
      },
    };
    onUpdate({ ...offer, extn: newExtn, updatedAt: new Date() });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Card className={`transition-all duration-200 ${isExpanded ? 'ring-2 ring-primary/20 shadow-lg' : 'hover:shadow-md'
      } ${offer.isRecommended ? 'border-green-200 bg-green-50/30' : ''}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              {/* Retailer Logo */}
              {currentRetailer && currentRetailer.logo ? (
                <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-white border-2 shadow-sm">
                  <img
                    src={currentRetailer.logo}
                    alt={`${currentRetailer.name} logo`}
                    className="max-w-full max-h-8 object-contain"
                  />
                </div>
              ) : (
                <Building className="w-12 h-12 p-2 text-primary bg-gray-100 rounded-lg" />
              )}

              <div>
                <h3 className="font-semibold text-lg">
                  {offer.retailerName || `Offer ${index + 1}`}
                </h3>

              </div>
            </div>


            <Badge variant="outline" className="capitalize">
              {offer.offerType}
            </Badge>
          </div>

          <div className="flex items-center gap-2">
            {/* Action Buttons */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCopy(offer.id)}
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              <Copy className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (window.confirm(`Delete offer for ${offer.retailerName}? This cannot be undone.`)) {
                  onRemove(offer.id);
                }
              }}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              title="Delete offer"
            >
              <Trash2 className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => onToggleExpansion(offer.id)}
              className="text-gray-600 hover:text-gray-700"
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Enhanced Summary Info with Brand Colors */}
        <div className="flex items-center gap-6 text-sm text-gray-600 mt-4">

          {offer.greenPercent > 0 && (
            <div className="flex items-center gap-1">
              <Leaf className="w-4 h-4 text-green-600" />
              <span>{offer.greenPercent}% green energy</span>
            </div>
          )}

          {offer.estimatedAnnualCost && (
            <div className="flex items-center gap-1">
              <DollarSign className="w-4 h-4" />
              <span>{formatCurrency(offer.estimatedAnnualCost)}/year</span>
            </div>
          )}
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          <div className="space-y-6">
            {/* Retailer Dropdown - From Database */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700">
                Energy Retailer <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.retailerId || undefined}
                onValueChange={(value) => {
                  const retailer = retailers.find(r => r.id === value);
                  if (retailer) {
                    // Update both retailerId and retailerName in one call
                    const newFormData = {
                      ...formData,
                      retailerId: value,
                      retailerName: retailer.name
                    };
                    setFormData(newFormData);

                    // Update the offer immediately with both values
                    const updatedOffer: Offer = {
                      ...offer,
                      retailerId: value,
                      retailerName: retailer.name,
                      offerType: newFormData.offerType as 'electricity' | 'gas',
                      termMonths: newFormData.termMonths,
                      greenPercent: newFormData.greenPercent,
                      updatedAt: new Date(),
                    };

                    console.log('OfferManagementCard - Retailer selected:', {
                      retailerId: value,
                      retailerName: retailer.name,
                      updatedOffer
                    });

                    onUpdate(updatedOffer);
                  }
                }}
                disabled={loadingRetailers}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingRetailers ? "Loading retailers..." : formData.retailerName || "Select energy retailer"} />
                </SelectTrigger>
                <SelectContent>
                  {displayRetailers.length === 0 && !loadingRetailers ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No retailers available
                    </div>
                  ) : (
                    displayRetailers.map((retailer) => {
                      const isInactive = !retailers.find(r => r.id === retailer.id);
                      return (
                        <SelectItem key={retailer.id} value={retailer.id}>
                          <div className="flex items-center gap-2">
                            {retailer.logo && (
                              <img src={retailer.logo} alt={retailer.name} className="w-5 h-5 object-contain" />
                            )}
                            <span>{retailer.name}</span>
                            {isInactive && (
                              <span className="text-xs text-muted-foreground">(Inactive)</span>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>



            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              {/* Offer Type */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Offer Type <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.offerType} onValueChange={(value) => updateFormData('offerType', value)}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="electricity">Electricity</SelectItem>
                    <SelectItem value="gas">Gas</SelectItem>
                  </SelectContent>
                </Select>
              </div>





              {/* Green Percent */}
              <div>
                <Label className="text-sm font-medium text-gray-700">
                  Green Energy % <Leaf className="w-4 h-4 inline text-green-600" />
                </Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.greenPercent}
                  onChange={(e) => updateFormData('greenPercent', parseFloat(e.target.value) || 0)}
                  className="mt-2"
                />
              </div>
            </div>



            {/* Multi-Year Rates & Charges - Side by Side Smooth & Stepped */}
            <DualRateTable
              contractYears={contractYears}
              // Smooth data
              smoothPeakRates={multiYearRateData.smooth.peakRates}
              smoothOffpeakRates={multiYearRateData.smooth.offpeakRates}
              smoothShoulderRates={multiYearRateData.smooth.shoulderRates}
              smoothEnvironmentalCharges={multiYearRateData.smooth.environmentalCharges}
              smoothNonEnergyCharges={multiYearRateData.smooth.nonEnergyCharges}
              // Stepped data
              steppedPeakRates={multiYearRateData.stepped.peakRates}
              steppedOffpeakRates={multiYearRateData.stepped.offpeakRates}
              steppedShoulderRates={multiYearRateData.stepped.shoulderRates}
              steppedEnvironmentalCharges={multiYearRateData.stepped.environmentalCharges}
              steppedNonEnergyCharges={multiYearRateData.stepped.nonEnergyCharges}
              // Event handlers
              onRateChange={handleRateChange}
              onEnvironmentalChargeChange={handleEnvironmentalChargeChange}
              onNonEnergyChargeChange={handleNonEnergyChargeChange}
              onAddCustomCharge={handleAddCustomCharge}
              onRemoveCustomCharge={handleRemoveCustomCharge}
              retailer={currentRetailer}
              // Per-contract-term data (from parsed offer)
              contractTermsData={offer.extn?.contract_terms || undefined}
              onContractTermRateChange={handleContractTermRateChange}
              selectedTermMonths={offer.termMonths}
            />


          </div>
        </CardContent>
      )}

    </Card>
  );
};

export default OfferManagementCard;
